/**
 * Meeting schemas — internal + client meetings with staff/external attendees.
 *
 * A meeting has an organizer (user), optional client/engagement links, and a
 * flat attendee list where each row is either an internal user or a free-text
 * external email. Times are stored/compared in UTC; the web renders PKT.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, emailSchema, urlSchema, booleanQuerySchema } from './common';

export const MEETING_STATUSES = ['SCHEDULED', 'DONE', 'CANCELLED'] as const;
export const meetingStatusSchema = z.enum(MEETING_STATUSES);
export type MeetingStatus = z.infer<typeof meetingStatusSchema>;

export const MEETING_STATUS_LABELS: Record<MeetingStatus, string> = {
  SCHEDULED: 'Scheduled',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};

export const RSVP_STATUSES = ['PENDING', 'ACCEPTED', 'DECLINED'] as const;
export const rsvpStatusSchema = z.enum(RSVP_STATUSES);
export type RsvpStatus = z.infer<typeof rsvpStatusSchema>;

export const meetingAttendeeOutputSchema = z.object({
  id: idSchema,
  userId: idSchema.nullable(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  email: z.string().nullable(),
  reminderSentAt: iso8601Schema.nullable(),
  rsvpStatus: rsvpStatusSchema,
  rsvpAt: iso8601Schema.nullable(),
});
export type MeetingAttendeeOutput = z.infer<typeof meetingAttendeeOutputSchema>;

export const meetingOutputSchema = z.object({
  id: idSchema,
  title: z.string(),
  startAt: iso8601Schema,
  durationMin: z.number().int(),
  location: z.string().nullable(),
  meetingUrl: z.string().nullable(),
  notes: z.string().nullable(),
  status: meetingStatusSchema,
  organizerId: idSchema,
  organizerName: z.string(),
  clientId: idSchema.nullable(),
  clientName: z.string().nullable(),
  engagementId: idSchema.nullable(),
  engagementTitle: z.string().nullable(),
  attendees: z.array(meetingAttendeeOutputSchema),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type MeetingOutput = z.infer<typeof meetingOutputSchema>;

export const createMeetingInputSchema = z.object({
  title: z.string().min(1).max(200),
  /** ISO datetime of the start instant. */
  startAt: iso8601Schema,
  durationMin: z.number().int().min(5).max(24 * 60).default(30),
  location: z.string().max(300).nullable().optional(),
  meetingUrl: urlSchema.nullable().optional(),
  notes: z.string().max(20000).nullable().optional(),
  clientId: idSchema.nullable().optional(),
  engagementId: idSchema.nullable().optional(),
  /** Internal staff attendees (user ids); the organizer is added implicitly. */
  attendeeUserIds: z.array(idSchema).max(50).default([]),
  /** External attendees — free-text emails, no accounts required. */
  externalEmails: z.array(emailSchema).max(50).default([]),
});
export type CreateMeetingInput = z.infer<typeof createMeetingInputSchema>;

export const updateMeetingInputSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  startAt: iso8601Schema.optional(),
  durationMin: z.number().int().min(5).max(24 * 60).optional(),
  location: z.string().max(300).nullable().optional(),
  meetingUrl: urlSchema.nullable().optional(),
  notes: z.string().max(20000).nullable().optional(),
  clientId: idSchema.nullable().optional(),
  engagementId: idSchema.nullable().optional(),
  /** Full replacement sets when provided. */
  attendeeUserIds: z.array(idSchema).max(50).optional(),
  externalEmails: z.array(emailSchema).max(50).optional(),
});
export type UpdateMeetingInput = z.infer<typeof updateMeetingInputSchema>;

export const listMeetingsInputSchema = z.object({
  /** Range filter on startAt (ISO). Both optional; unbounded side = open range. */
  from: iso8601Schema.optional(),
  to: iso8601Schema.optional(),
  status: meetingStatusSchema.optional(),
  clientId: idSchema.optional(),
  engagementId: idSchema.optional(),
  /** Only meetings I organize or attend. */
  mine: booleanQuerySchema.optional(),
  search: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(500),
});
export type ListMeetingsInput = z.infer<typeof listMeetingsInputSchema>;
